'use client';

import { useState } from 'react';
import Link from 'next/link';
import { MenuItem } from '@/utils/drupal-api/__generated__/graphql';
import {
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from '@headlessui/react';
import clsx from 'clsx';

const HomepageHeader = ({ menu }: { menu: MenuItem[] }) => {
  return (
    <PopoverGroup className="flex flex-initial items-center justify-start">
      <div className="relative flex w-full max-w-screen-2xl items-center justify-between gap-6 xl:gap-20">
        {menu.map((category, categoryIdx) => (
          <HoverablePopover
            last={categoryIdx + 1 === menu.length}
            key={`${category.title}_${categoryIdx}_menu-item`}
            category={category}
          />
        ))}
      </div>
    </PopoverGroup>
  );
};

const closeDelay = 100; // Delay before closing the popover (optional)

const HoverablePopover = ({
  category,
  last,
}: {
  category: MenuItem;
  last?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  let timeout: any; // For managing hover delay (optional)
  const openPopoverOnHover = () => {
    return {
      onMouseEnter: () => {
        if (timeout) {
          clearTimeout(timeout);
        }
        setOpen(true); // Open the popover on hover
      },
      onMouseLeave: () => {
        timeout = setTimeout(() => setOpen(false), closeDelay); // Optional delay before closing
      },
    };
  };
  return (
    <Popover className="flex">
      <div className="relative flex">
        <PopoverButton
          {...openPopoverOnHover()}
          role="menu"
          as="div"
          className={clsx(
            'group relative z-20 mx-auto flex w-full max-w-2xl items-center border-b-4 border-transparent py-[27px] text-primary-main transition-colors duration-200 ease-out hover:text-secondary-main focus-visible:outline-0',
            open && '!border-secondary-main !text-primary-main'
          )}
        >
          <Link
            role="menuitem"
            href={category.url || ''}
            className="text-inherit !no-underline"
          >
            <span className="whitespace-nowrap text-sm font-semibold uppercase tracking-[0.05em] text-inherit xl:text-[15px]">
              {category.title}
            </span>
          </Link>
        </PopoverButton>
        {open && (
          <PopoverPanel
            static
            {...openPopoverOnHover()}
            className={clsx(
              last && 'right-0',
              'absolute z-20 mb-[0.25rem] mt-[83px] w-auto min-w-[15rem] bg-white text-gray-500 shadow-lg'
            )}
          >
            <div className="flex flex-col divide-y">
              {category.children.map((item) => (
                <div
                  key={item.title}
                  className="flex w-full px-[0.75rem] py-[0.375rem]"
                >
                  <Link
                    href={item?.url || ''}
                    className="ut-sm w-full whitespace-nowrap text-sm text-black-1 !no-underline hover:text-secondary-main xl:text-[15px]"
                  >
                    {item.title}
                  </Link>
                </div>
              ))}
            </div>
          </PopoverPanel>
        )}
      </div>
    </Popover>
  );
};

export default HomepageHeader;
